/* eslint-disable max-lines -- the rule was disabled before a comment became required */
import {
  CancellationStatus,
  type DocumentsToDownload,
  type NowDocumentsToDownload,
  type UserDocumentPosition,
  type UserDocumentPositionERx,
  type UserOrderDocument,
  type UserOrderDocumentERx,
  type UserOrderResponseV2,
  type UserOrderResponseWithFlags,
  type UserOrderResponseWithFlagsSubOrdersInner,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { type PublicGlobalConfig } from '../../global-config'
import {
  urlResolverGetInvoiceByOrderId,
  urlResolverGetMarketPlaceOrderInvoiceByOrderId,
  urlResolverGetNowOrderInvoiceByOrderId,
  urlResolverGetSubOrderInvoicesBySubOrderId,
  urlResolverRequestMarketPlaceOrderInvoiceByOrderId,
} from '../../url-handling'

import {
  ORDER_STATUS_ENUM,
  type OrderStatusConfigLabels,
  OrderStatusLabel,
  ORDER_STATUS_ENUM as Status,
} from './AccountOverviewLastOrderShippingStatusTypes'
import { DOCUMENT_TYPE_INVOICE } from './DOCUMENT_TYPE_INVOICE'
import { SELLER_ID_SAE } from './SELLER_ID_SAE'

export const userSubOrderAccessorIsOrderDelivered = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean => subOrder.status === 'delivered'

export const userSubOrderAccessorIsCancellationRequested = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean =>
  subOrder.cancellationStatus === CancellationStatus.Requested ||
  subOrder.cancellationStatus === CancellationStatus.PartialRequested

export const userSubOrderAccessorGetInvoiceItems = (
  subOrder: UserSubOrderV2,
): (DocumentsToDownload | NowDocumentsToDownload)[] => {
  const subOrdersWithInvoice = subOrder.documentsToDownload?.filter((item) => {
    return item.type?.toLowerCase() === DOCUMENT_TYPE_INVOICE
  })

  const nowOrderSubOrderWithInvoice = subOrder.nowDocumentsToDownload?.filter(
    (item) => {
      return item.documentType?.toLowerCase() === DOCUMENT_TYPE_INVOICE
    },
  )

  return [
    ...(nowOrderSubOrderWithInvoice ?? []),
    ...(subOrdersWithInvoice ?? []),
  ]
}

type OrderDocument = {
  hashedToken?: string
  isERx?: boolean
  itemName?: string
  itemQuantity?: string
  offerId?: string
  pzn?: string
  subOrderId?: string
  thumbnailURL?: string
}

const getItemsAndThumbnailsFromOrderDocuments = (
  orderDocuments: UserOrderDocument[],
): OrderDocument[] => {
  return orderDocuments.flatMap((orderDocument) => {
    if (orderDocument.documentPositions) {
      return orderDocument.documentPositions.map((item) => {
        const quantity = [
          `${item.packagingQuantity} ${item.packagingUnit}`,
          item.pharmaForm,
        ]
          .filter(Boolean)
          .join(' | ')

        return {
          itemName: item.itemName,
          itemQuantity: quantity,
          offerId: item.offerId,
          pzn: item.pzn,
          thumbnailURL: item.thumbnailURL,
        }
      })
    }

    return []
  })
}

const getItemsAndThumbnailsFromOrderDocumentsErx = (
  orderDocumentsErx: UserOrderDocumentERx[],
): OrderDocument[] => {
  return orderDocumentsErx.flatMap((orderDocumentErx) => {
    if (orderDocumentErx.documentPositions) {
      return orderDocumentErx.documentPositions.map((item) => {
        //The construction of the quantity is as a result of the requirements of the ticket (WSCNL-9487)
        // as seen in figma here:- (https://www.figma.com/design/uuADxtSwdpdIe9XFWt62BI/branch/7XzfyuqvXWKCcx8oYNOdJL/Help-Center?node-id=6286-11988&m=dev)
        //e.g 120 St | Hartkapseln -> 'Packaging Quantity' 'Packaging Unit' |  'Form' (Injection/tablet etc)
        const quantity = [
          `${item.packagingQuantity} ${item.originalPrescriptionProduct?.packagingUnit}`,
          item.pharmaForm,
        ]
          .filter(Boolean)
          .join(' | ')

        return {
          hashedToken: item.hashedToken,
          isERx: true,
          itemName: item.itemName,
          itemQuantity: quantity,
          pzn: item.pzn,
        }
      })
    }

    return []
  })
}

const getOrderDocumentsWithSubOrderId = (
  orderDocuments: UserOrderDocument[],
  subOrderId: string,
): OrderDocument[] => {
  const orderDocumentItems =
    getItemsAndThumbnailsFromOrderDocuments(orderDocuments)

  return orderDocumentItems.map((item) => ({
    ...item,
    subOrderId,
  }))
}
const getOrderDocumentsErxWithSubOrderId = (
  orderDocumentsERx: UserOrderDocumentERx[],
  subOrderId: string,
): OrderDocument[] => {
  const orderDocumentErxItems =
    getItemsAndThumbnailsFromOrderDocumentsErx(orderDocumentsERx)

  return orderDocumentErxItems.map((item) => ({
    ...item,
    subOrderId,
  }))
}

export const userSubOrderAccessorGetSubOrdersDocumentsWithSubOrderId = (
  orderData: UserOrderResponseWithFlags | UserOrderResponseV2,
): OrderDocument[] => {
  const orderDocumentItems: OrderDocument[] = []

  if (!orderData.subOrders) {
    return orderDocumentItems
  }

  for (const subOrder of orderData.subOrders) {
    if (
      subOrder.orderDocuments?.length === 0 &&
      subOrder.orderDocumentsERx?.length === 0
    ) {
      continue
    }

    if (
      subOrder.orderDocuments &&
      subOrder.orderDocuments.length > 0 &&
      subOrder.subOrderId
    ) {
      const { orderDocuments, subOrderId } = subOrder
      const orderDocumentsWithSubOrderId = getOrderDocumentsWithSubOrderId(
        orderDocuments,
        subOrderId,
      )
      orderDocumentItems.push(...orderDocumentsWithSubOrderId)
    }

    if (
      subOrder.orderDocumentsERx &&
      subOrder.orderDocumentsERx.length > 0 &&
      subOrder.subOrderId
    ) {
      const { orderDocumentsERx, subOrderId } = subOrder
      const orderDocumentsErxWithSubOrderId =
        getOrderDocumentsErxWithSubOrderId(orderDocumentsERx, subOrderId)
      orderDocumentItems.push(...orderDocumentsErxWithSubOrderId)
    }
  }

  return orderDocumentItems
}

export const userSubOrderAccessorGetMultipleProductThumbnailAndName = (
  order: UserOrderResponseV2 | UserOrderResponseWithFlags,
): (UserDocumentPosition & UserDocumentPositionERx)[] => {
  const { subOrders } = order
  if (!subOrders?.length) {
    return []
  }

  return subOrders.flatMap((subOrder) => [
    ...(subOrder.orderDocuments?.length
      ? getItemsAndThumbnailsFromOrderDocuments(subOrder.orderDocuments)
      : []),
    ...(subOrder.orderDocumentsERx?.length
      ? getItemsAndThumbnailsFromOrderDocumentsErx(subOrder.orderDocumentsERx)
      : []),
  ])
}

const userSubOrderAccessorIsCancelled = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean => subOrder.status === ORDER_STATUS_ENUM.CANCELED

const userSubOrderAccessorIsClosed = (
  subOrders: UserSubOrderV2[] | UserOrderResponseWithFlagsSubOrdersInner[],
): boolean =>
  subOrders.every(
    (subOrder) =>
      subOrder.status === Status.CANCELED || subOrder.status === Status.CLOSED,
  )

const userSubOrderAccessorIsSellerSae = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean => subOrder.sellerId === SELLER_ID_SAE

const userSubOrderAccessorsNotClosed = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean =>
  subOrder.status !== ORDER_STATUS_ENUM.CANCELED &&
  subOrder.status !== ORDER_STATUS_ENUM.CLOSED

export const userSubOrderAccessorIsNowPickupOrder = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean => {
  if (!subOrder.carrier?.nowData) {
    return false
  }

  return 'slotPickup' in subOrder.carrier.nowData
}

const userSubOrderAccessorIsNowPickupCompleted = (
  subOrders: UserSubOrderV2[] | UserOrderResponseWithFlagsSubOrdersInner[],
): boolean =>
  subOrders.length === 1 &&
  subOrders.some(userSubOrderAccessorIsNowPickupOrder) &&
  subOrders[0].status === Status.CLOSED

const userSubOrderAccessorIsNowReadyForPickup = (
  subOrders: UserSubOrderV2[] | UserOrderResponseWithFlagsSubOrdersInner[],
): boolean =>
  subOrders.length === 1 &&
  subOrders.some(userSubOrderAccessorIsNowPickupOrder) &&
  subOrders[0].status === Status.DISPATCHED

/**
 *  Logic taken from Fock
 * https://github.com/RedTecLab/fock/blob/eb24fd6b5e0d6da412a7ca2eb34fb515796d88bc/app/shared/services/api/models/orders/orderResponse.ts#L68
 */
// eslint-disable-next-line max-statements -- the rule was disabled before a comment became required
export const userSubOrderAccessorGetStatus = (
  subOrders:
    | UserSubOrderV2[]
    | UserOrderResponseWithFlagsSubOrdersInner[]
    | undefined,
  index: number,
): string => {
  if (!subOrders || subOrders.length === 0) {
    return ORDER_STATUS_ENUM.PROCESSING
  }

  const currentSubOrder = subOrders[index]

  if (userSubOrderAccessorIsNowReadyForPickup(subOrders)) {
    return ORDER_STATUS_ENUM.READY_FOR_PICKUP
  }

  if (userSubOrderAccessorIsNowPickupCompleted(subOrders)) {
    return ORDER_STATUS_ENUM.COMPLETED
  }

  if (userSubOrderAccessorIsCancellationRequested(currentSubOrder)) {
    return ORDER_STATUS_ENUM.CANCEL_REQUESTED
  }

  if (
    userSubOrderAccessorIsSellerSae(currentSubOrder) &&
    currentSubOrder.status
  ) {
    return currentSubOrder.status
  }

  if (userSubOrderAccessorIsCancelled(currentSubOrder)) {
    return ORDER_STATUS_ENUM.CANCELED
  }

  if (userSubOrderAccessorIsClosed(subOrders)) {
    return ORDER_STATUS_ENUM.CLOSED
  }

  if (
    userSubOrderAccessorIsNowPickupOrder(currentSubOrder) &&
    userSubOrderAccessorsNotClosed(currentSubOrder)
  ) {
    if (currentSubOrder.status === ORDER_STATUS_ENUM.DISPATCHED) {
      return ORDER_STATUS_ENUM.READY_FOR_PICKUP
    } else if (currentSubOrder.status === ORDER_STATUS_ENUM.CLOSED) {
      return ORDER_STATUS_ENUM.COMPLETED
    }
  }

  return currentSubOrder.status ?? ORDER_STATUS_ENUM.PROCESSING
}

export const userSubOrderAccessorGetStatusBasedOnEstimatedDate = ({
  estimatedDate,
  orderDeliveredStatus,
  subOrderStatus,
  timeRange,
}: {
  estimatedDate?: string
  orderDeliveredStatus?: boolean
  subOrderStatus: string
  timeRange?: string | false
}): OrderStatusConfigLabels => {
  if (subOrderStatus !== OrderStatusLabel.Dispatched) {
    return (
      Object.values(OrderStatusLabel).find(
        (status) => status === subOrderStatus,
      ) ?? OrderStatusLabel.Processing
    )
  }

  if (orderDeliveredStatus) {
    return OrderStatusLabel.Delivered
  }

  const isEstimatedDateOrTimeRange = Boolean(estimatedDate ?? timeRange)

  return isEstimatedDateOrTimeRange ? 'shipped' : 'dispatched'
}

export const getIsOrderShipped = (subOrder: UserSubOrderV2): boolean => {
  const { status } = subOrder

  if (!status) {
    return false
  }

  return [ORDER_STATUS_ENUM.CLOSED, ORDER_STATUS_ENUM.DISPATCHED].includes(
    status as ORDER_STATUS_ENUM,
  )
}

export const getUrlForSingleInvoice = (
  invoice: DocumentsToDownload | NowDocumentsToDownload,
  config: PublicGlobalConfig,
  subOrderId: string,
): string | undefined => {
  if (
    'content' in invoice &&
    invoice.content &&
    'hash' in invoice.content &&
    invoice.content.hash
  ) {
    return urlResolverGetInvoiceByOrderId(
      config,
      subOrderId,
      invoice.content.hash,
    )
  }

  if ('documentId' in invoice && invoice.documentId && invoice.orderId) {
    return urlResolverGetNowOrderInvoiceByOrderId(config, {
      documentId: invoice.documentId,
      orderId: invoice.orderId,
      subOrderId,
    })
  }
  if (
    'content' in invoice &&
    invoice.content &&
    'document_id' in invoice.content &&
    invoice.content.document_id &&
    invoice.order_id
  ) {
    return urlResolverGetMarketPlaceOrderInvoiceByOrderId(config, {
      documentId: invoice.content.document_id,
      subOrderId,
    })
  }

  return undefined
}

const getUrlForMultipleInvoices = (
  invoices: (DocumentsToDownload | NowDocumentsToDownload)[],
  config: PublicGlobalConfig,
  subOrder: UserSubOrderV2,
): string | undefined => {
  if (!subOrder.subOrderId) {
    return undefined
  }
  if (subOrder.documentsToDownload?.length) {
    const [{ order_id: orderId }] = subOrder.documentsToDownload

    return (
      orderId &&
      urlResolverGetSubOrderInvoicesBySubOrderId(
        config,
        subOrder.subOrderId,
        orderId,
      )
    )
  }
  if (subOrder.nowDocumentsToDownload?.length) {
    const [{ orderId }] = subOrder.nowDocumentsToDownload

    return (
      orderId &&
      urlResolverGetSubOrderInvoicesBySubOrderId(
        config,
        subOrder.subOrderId,
        orderId,
      )
    )
  }

  return undefined
}

export const userSubOrderAccessorGetUrlForSingleOrMultipleInvoices = (
  subOrder: UserSubOrderV2,
  config: PublicGlobalConfig,
  orderId: string,
): string | undefined => {
  const invoices = userSubOrderAccessorGetInvoiceItems(subOrder)
  if (!subOrder.subOrderId) {
    return undefined
  }

  if (invoices.length === 0 && subOrder.sellerId !== SELLER_ID_SAE) {
    return urlResolverRequestMarketPlaceOrderInvoiceByOrderId(config, {
      orderId,
      subOrderId: subOrder.subOrderId,
    })
  }

  if (invoices.length === 1) {
    return getUrlForSingleInvoice(invoices[0], config, subOrder.subOrderId)
  }

  if (invoices.length > 1) {
    return getUrlForMultipleInvoices(invoices, config, subOrder)
  }

  return undefined
}

export const userSubOrderAccessorIsNow = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): boolean => {
  if (!subOrder.carrier?.nowData) {
    return false
  }

  return (
    'slot' in subOrder.carrier.nowData ||
    userSubOrderAccessorIsNowPickupOrder(subOrder)
  )
}

export const userSubOrderAccessorIsSellerMarketplace = (
  subOrder: UserSubOrderV2,
): boolean =>
  subOrder.sellerId !== SELLER_ID_SAE && !userSubOrderAccessorIsNow(subOrder)

export const userSubOrderGetNowOrderTimeRange = (
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner,
): string | undefined => {
  if (userSubOrderAccessorIsNow(subOrder) && subOrder.carrier?.nowData) {
    if ('slot' in subOrder.carrier.nowData) {
      return subOrder.carrier.nowData.slot?.timeString
    }

    if ('slotPickup' in subOrder.carrier.nowData) {
      return subOrder.carrier.nowData.slotPickup?.timeString
    }
  }

  return undefined
}

const userSubOrderGetOrderDocuments = (
  subOrder: UserSubOrderV2,
): UserOrderDocument[] => {
  if (subOrder.orderDocuments) {
    return subOrder.orderDocuments
  }
  throw new Error('orderDocuments is undefined')
}

const userSubOrderGetDocumentPositionFromOrderDocument = (
  orderDocument: UserOrderDocument,
): UserDocumentPosition[] => {
  if (orderDocument.documentPositions) {
    return orderDocument.documentPositions
  }
  throw new Error('documentPositions is undefined')
}

export const userSubOrderGetIsSubOrderCancellablePerItem = (
  subOrder: UserSubOrderV2,
): boolean => {
  const orderDocuments = userSubOrderGetOrderDocuments(subOrder)
  const documentPositions: UserDocumentPosition[] = []

  for (const orderDoc of orderDocuments) {
    documentPositions.push(
      ...userSubOrderGetDocumentPositionFromOrderDocument(orderDoc),
    )
  }

  return documentPositions.some((docPosition) => docPosition.isCancellable)
}

type Claimables = {
  claimable: UserDocumentPosition[]
  nonClaimable: UserDocumentPosition[]
}

const userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocs = (
  subOrder: UserSubOrderV2,
): Claimables => {
  const { orderDocuments: orderItems = [] } = subOrder

  const claimableOrderItems: UserDocumentPosition[] = []
  const nonClaimableOrderItems: UserDocumentPosition[] = []

  for (const orderItem of orderItems) {
    const docPositions = orderItem.documentPositions ?? []
    for (const docPosition of docPositions) {
      const { isClaimable } = docPosition
      if (isClaimable) {
        claimableOrderItems.push(docPosition)
      } else {
        nonClaimableOrderItems.push(docPosition)
      }
    }
  }

  return {
    claimable: claimableOrderItems,
    nonClaimable: nonClaimableOrderItems,
  }
}

const userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocsErx = (
  subOrder: UserSubOrderV2,
): Claimables => {
  const { orderDocumentsERx: orderItemsErx = [] } = subOrder

  const claimableOrderItems: UserDocumentPosition[] = []
  const nonClaimableOrderItems: UserDocumentPosition[] = []

  for (const orderItemErx of orderItemsErx) {
    const docPositions = orderItemErx.documentPositions ?? []
    for (const docPosition of docPositions) {
      const { isClaimable } = docPosition
      if (isClaimable) {
        claimableOrderItems.push(docPosition)
      } else {
        nonClaimableOrderItems.push(docPosition)
      }
    }
  }

  return {
    claimable: claimableOrderItems,
    nonClaimable: nonClaimableOrderItems,
  }
}

export const userSubOrderAccessorGetDamagedProductsClaimables = (
  subOrder: UserSubOrderV2,
): Claimables => {
  const { claimable: orderDocsClaimable, nonClaimable: orderDocsNonClaimable } =
    userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocs(subOrder)

  const {
    claimable: orderDocsErxClaimable,
    nonClaimable: orderDocsErxNonClaimable,
  } = userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocsErx(subOrder)

  return {
    claimable: [...orderDocsClaimable, ...orderDocsErxClaimable],
    nonClaimable: [...orderDocsNonClaimable, ...orderDocsErxNonClaimable],
  }
}

export const userSubOrderAcessorGetSubOrderId = (
  subOrder: UserSubOrderV2,
): string => {
  if (!subOrder.subOrderId) {
    throw new Error('subOrderId not found in subOrder')
  }

  return subOrder.subOrderId
}
